import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import GradientIcon from 'components/ui/GradientIcon';

const GuaranteeSection = () => {
  // Mobile sliding state for guarantees section only
  const [currentGuaranteeIndex, setCurrentGuaranteeIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showSwipeHints, setShowSwipeHints] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const autoAdvanceTimer = useRef(null);

  const guarantees = [
    {
      icon: "Shield",
      title: "100% Plagiarism-Free",
      description: "Every assignment is written from scratch with comprehensive plagiarism reports",
      details: [
        "Original content creation only",
        "Advanced plagiarism detection",
        "Detailed similarity reports",
        "Source verification included"
      ],
      badge: "Quality Guarantee",
      color: "success",
      gradient: "emerald"
    },
    {
      icon: "Clock",
      title: "On-Time Delivery",
      description: "We guarantee delivery by your deadline or you get your money back",
      details: [
        "99.8% on-time delivery rate",
        "Buffer time for quality checks",
        "Early delivery when possible",
        "Deadline protection promise"
      ],
      badge: "Delivery Guarantee",
      color: "primary",
      gradient: "primary"
    },
    {
      icon: "DollarSign",
      title: "Money-Back Promise",
      description: "Not satisfied? Get a full refund with no questions asked",
      details: [
        "100% satisfaction guarantee",
        "Quick refund processing",
        "No hidden conditions",
        "Customer-first policy"
      ],
      badge: "Satisfaction Guarantee",
      color: "accent",
      gradient: "gold"
    },
    {
      icon: "RefreshCw",
      title: "Revisions",
      description: "We Revise until your assignment meets all requirements perfectly",
      details: [
        "3 Free Revisions",
        "Quick turnaround on changes",
        "Expert availability for edits",
        "Quality improvement focus"
      ],
      badge: "Revision Guarantee",
      color: "secondary",
      gradient: "violet"
    },
    {
      icon: "Lock",
      title: "Privacy Protection",
      description: "Your personal information and assignments are completely confidential",
      details: [
        "Anonymous order processing",
        "Secure data encryption",
        "No third-party sharing",
        "GDPR compliance certified"
      ],
      badge: "Privacy Guarantee",
      color: "warning",
      gradient: "sky"
    },
    {
      icon: "FileCheck",
      title: "Academic Integrity",
      description: "All work follows strict academic standards and ethical guidelines",
      details: [
        "Proper citation formatting",
        "Academic writing standards",
        "Ethical research practices",
        "Reference material guidance"
      ],
      badge: "Integrity Guarantee",
      color: "error",
      gradient: "rose"
    }
  ];

  const securityFeatures = [
    {
      icon: "CreditCard",
      title: "Secure Payments",
      description: "SSL-encrypted transactions through trusted payment processors",
      providers: ["PayPal", "Stripe", "Bank Transfer"],
      gradient: "primary"
    },
    {
      icon: "Database",
      title: "Data Protection",
      description: "Advanced encryption and secure servers protect your information",
      providers: ["256-bit SSL", "GDPR Compliant", "ISO Certified"],
      gradient: "ocean"
    },
    {
      icon: "UserCheck",
      title: "Identity Safety",
      description: "Anonymous processing ensures your academic privacy",
      providers: ["No Name Required", "Secure Communication", "Private Delivery"],
      gradient: "emerald"
    }
  ];

  const qualityProcess = [
    {
      step: 1,
      title: "Expert Assignment",
      description: "Matched with subject specialist",
      icon: "UserCheck",
      gradient: "primary"
    },
    {
      step: 2,
      title: "Research & Writing",
      description: "Original content creation",
      icon: "Edit",
      gradient: "violet"
    },
    {
      step: 3,
      title: "Quality Review",
      description: "Multi-level editorial check",
      icon: "Search",
      gradient: "gold"
    },
    {
      step: 4,
      title: "Plagiarism Scan",
      description: "Comprehensive originality check",
      icon: "Shield",
      gradient: "emerald"
    },
    {
      step: 5,
      title: "Final Approval",
      description: "Ready for delivery",
      icon: "CheckCircle",
      gradient: "success"
    }
  ];

  // Swipe detection for guarantees
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentGuaranteeIndex < guarantees.length - 1) {
      setCurrentGuaranteeIndex(prev => prev + 1);
      setShowSwipeHints(false);
      clearAutoAdvanceTimer();
    } else if (isRightSwipe && currentGuaranteeIndex > 0) {
      setCurrentGuaranteeIndex(prev => prev - 1);
      setShowSwipeHints(false);
      clearAutoAdvanceTimer();
    }
  };

  // Auto-advance functionality
  const clearAutoAdvanceTimer = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
  };

  const startAutoAdvance = () => {
    if (isPaused) return; // Don't start if paused
    
    clearAutoAdvanceTimer();
    autoAdvanceTimer.current = setTimeout(() => {
      if (!isPaused) { // Double check before advancing
        setCurrentGuaranteeIndex(prev => 
          prev < guarantees.length - 1 ? prev + 1 : 0
        );
      }
    }, 4000);
  };

  // Pause/resume handlers
  const handleMouseEnter = () => {
    setIsPaused(true);
    clearAutoAdvanceTimer();
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    if (isMobile) {
      startAutoAdvance();
    }
  };

  const handleTouchStart = (e) => {
    setIsPaused(true);
    clearAutoAdvanceTimer();
    onTouchStart(e);
  };

  const handleTouchEnd = (e) => {
    onTouchEnd(e);
    // Resume after a short delay to allow for swipe completion
    setTimeout(() => {
      setIsPaused(false);
      if (isMobile) {
        startAutoAdvance();
      }
    }, 1000);
  };

  // Mobile detection and auto-advance management
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      if (mobile && !isPaused) {
        startAutoAdvance();
        
        if (showSwipeHints) {
          const hintTimer = setTimeout(() => {
            setShowSwipeHints(false);
          }, 5000);
          return () => {
            clearTimeout(hintTimer);
            clearAutoAdvanceTimer();
          };
        }
      } else {
        clearAutoAdvanceTimer();
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearAutoAdvanceTimer();
    };
  }, [currentGuaranteeIndex, showSwipeHints, isPaused]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearAutoAdvanceTimer();
  }, []);

  return (
    <section className="py-20 bg-grid-pattern">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Our <span className="text-gradient">Iron-Clad Guarantees</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Your success and satisfaction are our top priorities. 
            Every order comes with comprehensive guarantees that protect your investment and peace of mind.
          </p>
        </motion.div>

        {/* Guarantees Grid - Responsive with Mobile Carousel */}
        <div className="mb-20 relative" style={{padding: '2rem 1rem', overflow: 'visible'}}>
          {/* Mobile Navigation Dots - Only show on mobile */}
          <div className="lg:hidden flex justify-center space-x-2 mb-6">
            {guarantees.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentGuaranteeIndex(index);
                  clearAutoAdvanceTimer();
                  setShowSwipeHints(false);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentGuaranteeIndex ? 'bg-primary w-6' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Mobile Swipe Hints - Only show on mobile */}
          {showSwipeHints && (
            <div className="lg:hidden absolute top-1/2 left-4 right-4 flex justify-between z-10 pointer-events-none">
              <motion.div
                animate={{ 
                  x: [-8, 8, -8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2,
                  ease: "easeInOut"
                }}
                className="bg-white/90 rounded-full p-1 shadow-lg"
              >
                <Icon name="ChevronLeft" size={20} className="text-primary" />
              </motion.div>
              <motion.div
                animate={{ 
                  x: [8, -8, 8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2,
                  ease: "easeInOut"
                }}
                className="bg-white/90 rounded-full p-1 shadow-lg"
              >
                <Icon name="ChevronRight" size={20} className="text-primary" />
              </motion.div>
            </div>
          )}

          {/* Unified Cards Container - Grid on Desktop, Carousel on Mobile */}
          <div 
            className="lg:grid lg:grid-cols-1 lg:sm:grid-cols-2 lg:lg:grid-cols-3 lg:gap-6 relative"
            style={{
              overflow: isMobile ? 'hidden' : 'visible',
              paddingTop: '1rem' // Extra space for badges
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <motion.div
              className="flex md:contents"
              animate={{ x: isMobile ? `${-currentGuaranteeIndex * 100}%` : 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {guarantees.map((guarantee, index) => (
                <div key={guarantee.title} className="w-full flex-shrink-0 lg:flex-shrink px-2 lg:px-0 relative" style={{overflow: 'visible'}}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="glass-card p-4 sm:p-6 hover:transform hover:-translate-y-2 transition-all duration-250 relative md:min-h-0 min-h-[400px]"
                    style={{overflow: 'visible', position: 'relative'}}
                  >
                    {/* Badge */}
                    <div 
                      className="absolute left-4"
                      style={{
                        top: '-0.75rem',
                        zIndex: 30,
                        position: 'absolute'
                      }}
                    >
                      <div className={`bg-${guarantee.color} text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg whitespace-nowrap`}>
                        {guarantee.badge}
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="mx-auto mb-4 mt-6">
                      <GradientIcon 
                        name={guarantee.icon} 
                        size={32} 
                        gradient={guarantee.gradient}
                        containerClassName="w-14 h-14 sm:w-16 sm:h-16 rounded-full"
                      />
                    </div>

                    {/* Content */}
                    <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-3 text-center">
                      {guarantee.title}
                    </h3>

                    <p className="text-text-secondary text-center mb-4 text-sm sm:text-base">
                      {guarantee.description}
                    </p>

                    {/* Details */}
                    <ul className="space-y-2">
                      {guarantee.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start space-x-2">
                          <Icon name="Check" size={14} className="text-success mt-1 flex-shrink-0" />
                          <span className="text-text-secondary text-xs sm:text-sm">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Security Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl sm:text-3xl font-bold text-text-primary text-center mb-8 sm:mb-12">
            Your Security is Our Priority
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-elevated p-4 sm:p-6 text-center"
              >
                <div className="mx-auto mb-4">
                  <GradientIcon 
                    name={feature.icon} 
                    size={32} 
                    gradient={feature.gradient}
                    containerClassName="w-12 h-12 sm:w-16 sm:h-16 rounded-full"
                  />
                </div>

                <h4 className="text-lg sm:text-xl font-semibold text-text-primary mb-3">
                  {feature.title}
                </h4>

                <p className="text-text-secondary mb-4 text-sm sm:text-base">
                  {feature.description}
                </p>

                <div className="flex flex-wrap justify-center gap-2">
                  {feature.providers.map((provider, providerIndex) => (
                    <span
                      key={providerIndex}
                      className="bg-primary/10 text-primary px-2 sm:px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {provider}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Quality Process */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl sm:text-3xl font-bold text-text-primary text-center mb-8 sm:mb-12">
            Our 5-Step Quality Assurance Process
          </h3>

          <div className="relative">
            {/* Static Background Line - Desktop Only */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-300/30 rounded-full transform -translate-y-1/2 hidden lg:block"></div>
            
            {/* Animated Progress Line - Desktop Only */}
            <motion.div 
              className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-primary via-secondary to-success rounded-full transform -translate-y-1/2 hidden lg:block"
              initial={{ width: 0 }}
              whileInView={{ width: '100%' }}
              transition={{ 
                duration: 4,
                ease: "easeInOut",
                delay: 0.5
              }}
              viewport={{ once: true }}
            ></motion.div>

            {/* Mobile: Vertical connecting lines */}
            <div className="lg:hidden absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300/30"></div>
            <motion.div 
              className="lg:hidden absolute left-8 top-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-success"
              initial={{ height: 0 }}
              whileInView={{ height: '100%' }}
              transition={{ 
                duration: 3,
                ease: "easeInOut",
                delay: 0.3
              }}
              viewport={{ once: true }}
            ></motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
              {qualityProcess.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 30, scale: 0.8 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: window.innerWidth >= 1024 ? 0.8 + (index * 0.3) : index * 0.2,
                    ease: "easeOut"
                  }}
                  viewport={{ once: true, margin: "-50px" }}
                  className="text-center lg:text-center text-left relative lg:relative pl-16 lg:pl-0"
                >
                  {/* Step Circle with Number */}
                  <motion.div 
                    className="relative mx-auto lg:mx-auto mx-0 mb-4 lg:mb-4 mb-6 absolute lg:relative left-0 lg:left-auto top-0 lg:top-auto"
                    style={{ left: window.innerWidth < 1024 ? '-3rem' : 'auto' }}
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    transition={{
                      duration: 0.5,
                      delay: window.innerWidth >= 1024 ? 0.8 + (index * 0.3) + 0.3 : index * 0.1,
                      ease: "easeOut"
                    }}
                    viewport={{ once: true, margin: "-30px" }}
                  >
                    <motion.div
                      initial={{ scale: 1 }}
                      whileInView={{ 
                        scale: [1, 1.05, 1],
                        boxShadow: window.innerWidth >= 1024 ? [
                          "0 0 0 0 rgba(59, 130, 246, 0)",
                          "0 0 0 10px rgba(59, 130, 246, 0.3)",
                          "0 0 0 0 rgba(59, 130, 246, 0)"
                        ] : "0 0 0 0 rgba(59, 130, 246, 0)"
                      }}
                      transition={{
                        duration: window.innerWidth >= 1024 ? 1.5 : 0.8,
                        delay: window.innerWidth >= 1024 ? 0.8 + (index * 0.3) + 0.5 : index * 0.1 + 0.3,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true, margin: "-30px" }}
                      className="rounded-full"
                    >
                      <GradientIcon 
                        name={step.icon} 
                        size={24} 
                        gradient={step.gradient}
                        containerClassName="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-background"
                      />
                    </motion.div>
                    {/* Step Number */}
                    <motion.div 
                      className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-accent rounded-full flex items-center justify-center text-white text-xs font-bold z-20"
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: window.innerWidth >= 1024 ? 0.8 + (index * 0.3) + 0.2 : index * 0.1 + 0.2,
                        ease: "backOut"
                      }}
                      viewport={{ once: true, margin: "-30px" }}
                    >
                      {step.step}
                    </motion.div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: window.innerWidth >= 1024 ? 0.8 + (index * 0.3) + 0.6 : index * 0.2 + 0.4,
                      ease: "easeOut"
                    }}
                    viewport={{ once: true, margin: "-30px" }}
                    className="lg:mt-0 mt-0"
                  >
                    <h4 className="text-base sm:text-lg font-semibold text-text-primary mb-2">
                      {step.title}
                    </h4>

                    <p className="text-text-secondary text-xs sm:text-sm">
                      {step.description}
                    </p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Final Guarantee CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-success/10 to-accent/10 p-8 rounded-2xl border border-success/20 mt-16"
        >
          <div className="mx-auto mb-4">
            <GradientIcon name="Award" size={48} gradient="success" containerClassName="w-16 h-16 rounded-full" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Risk-Free Academic Assistance
          </h3>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-6">
            With our comprehensive guarantees, you have nothing to lose and everything to gain. 
            Your academic success is protected by our commitment to quality, security, and satisfaction.
          </p>
          <div className="flex flex-wrap justify-center items-center space-x-6 mb-6 text-sm text-text-muted">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span>100% Satisfaction</span>
            </div>
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={16} className="text-primary" />
              <span>On-Time Delivery</span>
            </div>
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="RefreshCw" size={16} className="text-secondary" />
              <span>Free Revisions</span>
            </div>
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="DollarSign" size={16} className="text-accent" />
              <span>Money-Back Promise</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GuaranteeSection;
